import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import AddRoleModal from './AddRoleModal';
import ErrorModal from './ErrorModal';
import StatusBadge from './StatusBadge';
import Button from './ui/Button';
import { EmptyState, ErrorState } from './ui/States';
import { useConfirm } from './ui/ConfirmProvider';
import { createRole, deleteRole, getRoles, updateRole, Role } from '../lib/rolesApi';
import { useToast } from '../context/ToastContext';
import LoadingState from './LoadingState';
import { formatDate as fmtDate } from "../utils/dateFormat";

interface RoleManagementProps {
  onBackToUsers?: () => void;
}

const RoleManagement: React.FC<RoleManagementProps> = ({ onBackToUsers }) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [isAddRoleModalOpen, setIsAddRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const toast = useToast();
  const confirm = useConfirm();
  const [errorModal, setErrorModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
  }>({
    isOpen: false,
    title: '',
    message: ''
  });

  // Load roles on component mount
  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setIsLoading(true);
      const fetchedRoles = await getRoles();
      setRoles(fetchedRoles);
      setError('');
    } catch (err) {
      setError('Failed to load roles. Please try again.');
      console.error('Error loading roles:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddRole = async (roleData: any) => {
    try {
      const newRole = await createRole(roleData);
      setRoles([...roles, newRole]);
      setIsAddRoleModalOpen(false);
      setError('');
      toast.success('Role created successfully!');
    } catch (err: any) {
      setErrorModal({
        isOpen: true,
        title: 'Role Creation Failed',
        message: err.message || 'Failed to create role. Please try again.'
      });
    }
  };

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setIsAddRoleModalOpen(true);
  };

  const handleUpdateRole = async (roleData: any) => {
    if (editingRole) {
      try {
        const updatedRole = await updateRole(editingRole._id, roleData);
        setRoles(roles.map(role =>
          role._id === editingRole._id ? updatedRole : role
        ));
        setEditingRole(null);
        setIsAddRoleModalOpen(false);
        setError('');
        toast.success('Role updated successfully!');
      } catch (err: any) {
        setErrorModal({
          isOpen: true,
          title: 'Role Update Failed',
          message: err.message || 'Failed to update role. Please try again.'
        });
      }
    }
  };

  // This was a ConfirmationModal driven by its own open/target state. useConfirm is the
  // app-wide dialog, so the question and its red confirm button match every other
  // destructive action. setRoles takes the functional form because the await now spans a
  // user decision and the `roles` captured when the row was clicked can be stale by then.
  const handleDeleteRole = async (roleId: string) => {
    const role = roles.find(r => r._id === roleId);
    if (!role) return;

    const confirmed = await confirm({
      title: 'Are you sure?',
      summary: 'Deleting this role is permanent. You will not be able to restore it later.',
      tone: 'error',
      confirmLabel: 'Delete'
    });
    if (!confirmed) return;

    try {
      await deleteRole(roleId);
      setRoles(prev => prev.filter(r => r._id !== roleId));
      toast.success('Role deleted successfully!');
    } catch (err: any) {
      setErrorModal({
        isOpen: true,
        title: 'Role Deletion Failed',
        message: err.message || 'Failed to delete role. Please try again.'
      });
    }
  };

  // The loading state is a row inside the table rather than an early return, so the title
  // and its actions are on screen from the first paint instead of appearing a beat later.
  return (
    <div className="h-full flex flex-col bg-brand-bg">
      {/* Header */}
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <h1 className="text-xl font-semibold leading-7 text-slate-900">Roles</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Only offered when this view is the toggle inside User Management; on the
                standalone /roles shell there is nothing to go back to. */}
            {onBackToUsers && <Button onClick={onBackToUsers}>My users</Button>}
            <Button
              variant="primary"
              onClick={() => {
                setEditingRole(null);
                setIsAddRoleModalOpen(true);
              }}
              icon={<Plus aria-hidden="true" className="h-4 w-4" />}
            >
              Add role
            </Button>
          </div>
        </div>
      </div>

      {/* Roles Table. This was five independently stacked flex columns kept in step only
          by forcing every cell to exactly 34px, so any wrapped role name slid the columns
          out of alignment with each other. A real table keeps rows together by itself. */}
      <div className="flex-1 overflow-auto px-4 py-4 sm:px-8">
        <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-auto">
            <table className="data-table min-w-[720px] table-fixed">
              <colgroup>
                <col className="w-[34%]" />
                <col className="w-[20%]" />
                <col className="w-[20%]" />
                <col className="w-[13%]" />
                <col className="w-[13%]" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">Role name</th>
                  <th scope="col">Type</th>
                  <th scope="col">Created</th>
                  <th scope="col">Status</th>
                  <th scope="col" className="text-right">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => (
                  <tr key={role._id}>
                    <td className="truncate font-medium text-slate-900" title={role.displayName}>
                      {role.displayName}
                    </td>
                    <td>{role.isSystemRole ? 'System' : 'Custom'}</td>
                    <td className="whitespace-nowrap">{fmtDate(role.createdAt)}</td>
                    <td>
                      <StatusBadge active={role.isActive} />
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditRole(role)}
                          title="Edit role"
                          aria-label={`Edit ${role.displayName}`}
                          icon={<Pencil aria-hidden="true" className="h-4 w-4" />}
                        />
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteRole(role._id)}
                          title="Delete role"
                          aria-label={`Delete ${role.displayName}`}
                          icon={<Trash2 aria-hidden="true" className="h-4 w-4" />}
                        />
                      </div>
                    </td>
                  </tr>
                ))}

                {isLoading && (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center">
                      <LoadingState text="Loading roles..." />
                    </td>
                  </tr>
                )}

                {/* The load error was stored and never shown, so a failed fetch looked
                    exactly like an account with no roles defined. */}
                {!isLoading && error && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6">
                      <ErrorState message={error} onRetry={loadRoles} />
                    </td>
                  </tr>
                )}

                {!isLoading && !error && roles.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6">
                      <EmptyState title="No roles yet" detail="Add a role to group the permissions a user gets." />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add/Edit Role Modal */}
      {isAddRoleModalOpen && (
        <AddRoleModal
          onClose={() => {
            setIsAddRoleModalOpen(false);
            setEditingRole(null);
          }}
          onSubmit={editingRole ? handleUpdateRole : handleAddRole}
          initialData={editingRole}
        />
      )}

      {/* Deleting asks through useConfirm: see handleDeleteRole. */}

      {/* Error Modal */}
      <ErrorModal
        isOpen={errorModal.isOpen}
        title={errorModal.title}
        message={errorModal.message}
        onClose={() => setErrorModal({ isOpen: false, title: '', message: '' })}
      />
    </div>
  );
};

export default RoleManagement;
